/**
 * D312: Rename world, with the same rules as naming a new one (D267). D459: it renames any world
 * by id, from that world's own sheet; without an id it renames the open one.
 */
import { useState } from 'preact/hooks';
import { REASON_WAIT } from '../game/copy.ts';
import { Button } from '../ui/Button.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { useIdle } from './store.ts';
import type { IdleWorldEntry } from './sync.ts';

/** D267, D312: the longest a world's name may be, new or renamed. Matches the server's limit. */
export const WORLD_NAME_MAX = 40;

export function RenameWorldSheet({
  name,
  worlds,
  worldId,
  onClose,
}: {
  name: string;
  worlds: IdleWorldEntry[];
  worldId?: string;
  onClose: () => void;
}) {
  const [value, setValue] = useState(name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = value.trim();
  const key = (s: string) => s.trim().toLowerCase();
  const target = worldId ?? worlds.find((f) => f.current)?.worldId;
  const clash = worlds.some(
    (f) => f.worldId !== target && !f.archived && key(f.name) === key(trimmed),
  );
  const tooLong = trimmed.length > WORLD_NAME_MAX;
  const reason = !trimmed
    ? 'Name your world.'
    : tooLong
      ? `Keep it to ${WORLD_NAME_MAX} characters or fewer.`
      : clash
        ? 'You already have a world with that name.'
        : undefined;
  const save = async () => {
    if (reason || busy) return;
    setBusy(true);
    setError(null);
    const failed = await useIdle.getState().renameWorld(trimmed, worldId);
    setBusy(false);
    if (failed) return setError(failed);
    onClose();
  };
  return (
    <Sheet title="Rename world" onClose={onClose} testId="rename-world-sheet" zIndex={55}>
      <div style={{ display: 'grid', gap: 10 }}>
        <input
          value={value}
          data-testid="rename-world-input"
          aria-label="World name"
          autoComplete="off"
          maxLength={WORLD_NAME_MAX}
          onInput={(e) => setValue((e.currentTarget as HTMLInputElement).value)}
          style={{
            height: 44,
            padding: '0 12px',
            border: '1px solid var(--line)',
            borderRadius: 4,
            background: 'var(--surface)',
            color: 'var(--ink)',
            font: '400 16px/1.4 var(--font-ui)',
            outline: 'none',
          }}
        />
        {error && (
          <div style={{ font: '400 13px/1.4 var(--font-ui)', color: 'var(--red)' }}>{error}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!!reason || busy}
            {...(reason ? { reason } : { explain: REASON_WAIT })}
            onClick={() => void save()}
          >
            Save
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
